import type { Control } from "react-hook-form";
import { useI18nContext } from "~/i18n/i18n-react";
import type { SupportedLanguage } from "~/utils/supported-languages";
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../ui/form";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../ui/select";
import type { CreateRoomFormValues } from "./create-room-schema";

/**
 * The curated shortlist offered at room creation. Deliberately narrower than
 * `supportedLanguages`, which covers everything the STT service accepts.
 */
const ROOM_LANGUAGE_OPTIONS = [
	{ value: "auto", labelKey: "AUTO_DETECT" },
	{ value: "en-US", labelKey: "ENGLISH_US" },
	{ value: "ar-SA", labelKey: "ARABIC_KSA" },
	{ value: "es-CR", labelKey: "SPANISH_CR" },
	{ value: "ca-ES", labelKey: "CATALAN_ES" },
	{ value: "pt-PT", labelKey: "PORTUGUESE_PT" },
	{ value: "pt-BR", labelKey: "PORTUGUESE_BR" },
	{ value: "et-EE", labelKey: "ESTONIAN_EE" },
	{ value: "ru-RU", labelKey: "RUSSIAN_RU" },
	{ value: "mt-MT", labelKey: "MALTESE_MT" },
	{ value: "fil-PH", labelKey: "FILIPINO_PH" },
	{ value: "es-MX", labelKey: "SPANISH_MX" },
	{ value: "zh-CN", labelKey: "CHINESE_CN" },
	{ value: "vi-VN", labelKey: "VIETNAMESE_VN" },
	{ value: "ko-KR", labelKey: "KOREAN_KR" },
	{ value: "hi-IN", labelKey: "HINDI_IN" },
	{ value: "fa-IR", labelKey: "PERSIAN_IR" },
] as const satisfies readonly { value: SupportedLanguage; labelKey: string }[];

interface LanguageSelectFieldProps {
	control: Control<CreateRoomFormValues>;
}

export const LanguageSelectField = ({ control }: LanguageSelectFieldProps) => {
	const { LL } = useI18nContext();

	return (
		<FormField
			control={control}
			name="language"
			render={({ field }) => (
				<FormItem>
					<FormLabel>
						{LL.CREATE_ROOM_FORM.LANGUAGE()}
						{"*"}
					</FormLabel>
					<Select onValueChange={field.onChange} value={field.value}>
						<FormControl>
							<SelectTrigger>
								<SelectValue
									placeholder={LL.CREATE_ROOM_FORM.LANGUAGE_PLACEHOLDER()}
								/>
							</SelectTrigger>
						</FormControl>
						<SelectContent className="max-h-60 overflow-y-auto">
							{ROOM_LANGUAGE_OPTIONS.map(({ value, labelKey }) => (
								<SelectItem key={value} value={value}>
									{LL.CREATE_ROOM_FORM[labelKey]()}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<FormDescription>
						{LL.CREATE_ROOM_FORM.LANGUAGE_DESCRIPTION()}
					</FormDescription>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
};
